import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { STONE_PATTERN_CATEGORIES, STONE_PATTERN_LIBRARY } from "../../../shared/api-contract";
import type { StonePatternLibraryEntry } from "../../../shared/api-contract";
import { Icon } from "../../kit";
import { filterLibrary, libraryBand, thumbGeometry } from "../patternUi";
import type { ThumbGeometry } from "../patternUi";
import { PatternThumb } from "./PatternThumb";

/** One geometry per entry for the page's lifetime: the library is constant
 *  (ids are stable forever) and the whole set expands in well under 100 ms. */
const THUMB_CACHE = new Map<string, ThumbGeometry>();
export function libraryThumb(entry: StonePatternLibraryEntry): ThumbGeometry {
  let g = THUMB_CACHE.get(entry.id);
  if (!g) {
    g = thumbGeometry({ layers: entry.layers }, libraryBand(entry));
    THUMB_CACHE.set(entry.id, g);
  }
  return g;
}

/**
 * The library's contents: category chips, a search box and every entry as a
 * live thumbnail. Split from the floating shell so the host checks can render
 * it statically (a portal needs a document).
 */
export function PatternLibraryBody({
  currentId,
  onPick,
  initialCategory = null,
  initialQuery = "",
}: {
  currentId?: string;
  onPick: (id: string) => void;
  initialCategory?: string | null;
  initialQuery?: string;
}) {
  const [category, setCategory] = useState<string | null>(initialCategory);
  const [query, setQuery] = useState(initialQuery);
  const list = useMemo(() => filterLibrary(STONE_PATTERN_LIBRARY, category, query), [category, query]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <label className="flex items-center gap-2 rounded-lg border border-ed-border bg-white px-3 py-2 focus-within:border-ed-accent">
        <Icon name="search" size={16} className="text-ed-text-2" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search patterns — wave, trio, eternity…"
          aria-label="Search patterns"
          className="w-full bg-transparent font-body text-[12px] text-ed-text outline-none placeholder:text-ed-text-3"
        />
      </label>

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Pattern categories">
        <CategoryChip label="All" count={STONE_PATTERN_LIBRARY.length} on={category === null} onClick={() => setCategory(null)} />
        {STONE_PATTERN_CATEGORIES.map((c) => (
          <CategoryChip
            key={c}
            label={c}
            count={STONE_PATTERN_LIBRARY.filter((e) => e.category === c).length}
            on={category === c}
            onClick={() => setCategory(category === c ? null : c)}
          />
        ))}
      </div>

      <span className="font-body text-[11px] text-ed-text-2" data-library-count={list.length}>
        {list.length === STONE_PATTERN_LIBRARY.length
          ? `${list.length} patterns`
          : `${list.length} of ${STONE_PATTERN_LIBRARY.length} patterns`}
        {" · each one stays fully editable once picked"}
      </span>

      <div className="grid grid-cols-2 gap-2 pb-2">
        {list.map((e) => {
          const on = e.id === currentId;
          return (
            <button
              key={e.id}
              type="button"
              data-entry={e.id}
              title={`${e.name} — ${e.category}`}
              onClick={() => onPick(e.id)}
              className={`flex flex-col gap-1.5 rounded-lg border bg-white p-2 text-left transition-colors ${
                on ? "border-ed-accent ring-1 ring-ed-accent" : "border-ed-border hover:border-ed-text-3"
              }`}
            >
              <span className="flex h-[58px] items-center justify-center overflow-hidden rounded-md bg-[#f7f5f0] px-1">
                <PatternThumb pattern={null} band={libraryBand(e)} geometry={libraryThumb(e)} title={e.name} className="h-full w-full" />
              </span>
              <span className="truncate font-body text-[11.5px] font-medium text-ed-text">{e.name}</span>
              <span className="-mt-1 truncate font-body text-[11px] text-ed-text-2">
                {e.category} · {libraryThumb(e).stones.length} stones
              </span>
            </button>
          );
        })}
        {list.length === 0 ? (
          <span className="col-span-2 py-6 text-center font-body text-[12px] text-ed-text-2">
            Nothing matches. Try fewer words, or pick All.
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CategoryChip({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onClick}
      className={`rounded-full border px-2.5 py-1 font-body text-[11px] transition-colors ${
        on ? "border-ed-accent bg-ed-accent-weak/50 font-medium text-ed-text" : "border-ed-border bg-white text-ed-text-2 hover:border-ed-text-3"
      }`}
    >
      {label} <span className="text-ed-text-3">{count}</span>
    </button>
  );
}

/**
 * The library as a side panel floating over the canvas (z-99, the studio's
 * overlay rule: the canvas never gives up width, so the viewer never
 * reallocates its drawing buffer). Sits just right of the 440 px builder
 * column. Esc closes it.
 */
export function PatternLibraryPanel({
  currentId,
  onPick,
  onClose,
}: {
  currentId?: string;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => { if (ev.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <aside
      role="dialog"
      aria-label="Stone pattern library"
      className="fixed bottom-4 left-[452px] top-[72px] z-[99] flex w-[400px] max-w-[calc(100vw-468px)] flex-col gap-3 rounded-xl border border-ed-border bg-white p-4 shadow-[0_18px_50px_rgba(26,26,25,0.16)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="font-body text-[15px] font-semibold text-ed-text">Pattern library</h2>
          <p className="font-body text-[11.5px] text-ed-text-2">Pick one to start from. Every row, curve and size stays editable.</p>
        </div>
        <button
          type="button"
          aria-label="Close library"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ed-text-2 hover:bg-ed-accent-weak/40 hover:text-ed-text"
        >
          <Icon name="close" size={18} />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pr-1">
        <PatternLibraryBody currentId={currentId} onPick={onPick} />
      </div>
    </aside>,
    document.body,
  );
}
